import { useMutation } from "@apollo/client";
import { DownloadBox } from "atoms";
import {
    constantsData,
    identifiersData,
    namesData,
    statusTypesData,
} from "data";
import { exportArtistsMutation } from "graph";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { openAlert } from "redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "utils";

const useExportArtists = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportArtistsAction, { loading }] = useMutation(exportArtistsMutation);

    const {
        exportArtistsPending: exportArtistsPendingToastName,
        exportArtistsSuccess: exportArtistsSuccessToastName,
    } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportArtists = async (ids) => {
        setExporting(true);

        const randomId = getUniqueRandomId();

        try {
            await exportArtistsAction({
                onCompleted: ({ exportArtists: exportArtistsData }) => {
                    setExporting(false);

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportArtistsSuccessToastName}_${toastId}_${randomId}`}
                                link={exportArtistsData}
                                message={t(
                                    "messages.downloadMsgs.download",
                                    { entity: t("artists.name") },
                                )}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportArtistsSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(openAlert(
                        t(
                            "messages.exportMsgs.success",
                            { entity: t("artists.name") },
                        ),
                        statusTypesData.success,
                    ));
                },
                variables: {
                    artistsIds: ids,
                    toCsv: true,
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportArtistsPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(
                    () => t(
                        "messages.exportMsgs.pending",
                        { entity: t("artists.name") },
                    ),
                    { toastId: `${exportArtistsPendingToastName}_${toastId}` },
                );
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportArtists };
};

export default useExportArtists;
